import { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import {
  FaPlus, FaEdit, FaTrash, FaTimes, FaSave, FaUpload,
  FaAppleAlt, FaSeedling, FaHardHat, FaTruck, FaTshirt, FaFlask,
  FaCog, FaIndustry, FaTools, FaBoxes, FaLeaf, FaShieldAlt,
  FaWrench, FaRecycle, FaWarehouse, FaChartLine
} from 'react-icons/fa';

const ICON_LIST = [
  { name: 'FaAppleAlt', icon: FaAppleAlt, label: 'Gıda' },
  { name: 'FaSeedling', icon: FaSeedling, label: 'Tarım' },
  { name: 'FaHardHat', icon: FaHardHat, label: 'İnşaat' },
  { name: 'FaTruck', icon: FaTruck, label: 'Lojistik' },
  { name: 'FaTshirt', icon: FaTshirt, label: 'Tekstil' },
  { name: 'FaFlask', icon: FaFlask, label: 'Kimya' },
  { name: 'FaCog', icon: FaCog, label: 'Makine' },
  { name: 'FaIndustry', icon: FaIndustry, label: 'Sanayi' },
  { name: 'FaTools', icon: FaTools, label: 'Teknik' },
  { name: 'FaBoxes', icon: FaBoxes, label: 'Depo' },
  { name: 'FaLeaf', icon: FaLeaf, label: 'Ekoloji' },
  { name: 'FaShieldAlt', icon: FaShieldAlt, label: 'Güvenlik' },
  { name: 'FaWrench', icon: FaWrench, label: 'Bakım' },
  { name: 'FaRecycle', icon: FaRecycle, label: 'Geri Dön.' },
  { name: 'FaWarehouse', icon: FaWarehouse, label: 'Depolama' },
  { name: 'FaChartLine', icon: FaChartLine, label: 'Büyüme' },
];

const emptyForm = {
  name: '', slug: '', icon: 'FaCog', short_description: '',
  full_description: '', image_url: '', features: [],
  display_order: 0, is_active: 1
};

export default function AdminServices() {
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };
  const [services, setServices] = useState([]);
  const [modal, setModal] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');

  const load = () => axios.get('/api/services/all', { headers }).then(r => setServices(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const autoSlug = (name) => name.toLowerCase()
    .replace(/ğ/g, 'g').replace(/ü/g, 'u').replace(/ş/g, 's').replace(/ı/g, 'i').replace(/ö/g, 'o').replace(/ç/g, 'c')
    .replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');

  const addFeature = () => setForm(f => ({ ...f, features: [...f.features, ''] }));
  const removeFeature = (i) => setForm(f => ({ ...f, features: f.features.filter((_, idx) => idx !== i) }));
  const updateFeature = (i, val) => setForm(f => ({ ...f, features: f.features.map((v, idx) => idx === i ? val : v) }));

  const openAdd = () => { setForm(emptyForm); setEditId(null); setError(''); setModal(true); };
  const openEdit = (s) => {
    let features = [];
    if (s.features) {
      try { features = Array.isArray(s.features) ? s.features : JSON.parse(s.features); } catch {}
    }
    setForm({ ...s, features: Array.isArray(features) ? features : [], image_url: s.image_url || '' });
    setEditId(s.id); setError(''); setModal(true);
  };
  const close = () => { setModal(false); setError(''); };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData(); fd.append('image', file);
    try {
      const { data } = await axios.post('/api/upload', fd, { headers: { ...headers, 'Content-Type': 'multipart/form-data' } });
      setForm(f => ({ ...f, image_url: data.url }));
    } catch { alert('Yükleme başarısız'); }
    setUploading(false);
  };

  const handleSave = async () => {
    setSaving(true); setError('');
    const payload = { ...form, features: form.features.filter(f => f.trim()) };
    try {
      if (editId) await axios.put(`/api/services/${editId}`, payload, { headers });
      else await axios.post('/api/services', payload, { headers });
      load(); close();
    } catch (err) { setError(err.response?.data?.message || 'Hata oluştu'); }
    setSaving(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Bu hizmeti silmek istediğinize emin misiniz?')) return;
    await axios.delete(`/api/services/${id}`, { headers }); load();
  };

  const handleToggle = async (id) => {
    await axios.patch(`/api/services/${id}/toggle`, {}, { headers });
    load();
  };

  const selectedIconDef = ICON_LIST.find(i => i.name === form.icon) || ICON_LIST[6];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-primary">Hizmetler</h1>
          <p className="text-gray-500 text-sm">{services.length} hizmet</p>
        </div>
        <button onClick={openAdd} className="btn-primary text-sm py-2.5">
          <FaPlus /> Hizmet Ekle
        </button>
      </div>

      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b">
            <tr>
              <th className="px-5 py-3 text-left font-medium text-gray-500">Hizmet Adı</th>
              <th className="px-5 py-3 text-left font-medium text-gray-500 hidden sm:table-cell">URL Kodu</th>
              <th className="px-5 py-3 text-left font-medium text-gray-500 hidden md:table-cell">Sıra</th>
              <th className="px-5 py-3 text-left font-medium text-gray-500">Durum</th>
              <th className="px-5 py-3 text-right font-medium text-gray-500">İşlem</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {services.map(s => (
              <tr key={s.id} className="hover:bg-gray-50">
                <td className="px-5 py-3.5 font-medium text-gray-700">{s.name}</td>
                <td className="px-5 py-3.5 text-gray-400 text-xs font-mono hidden sm:table-cell">{s.slug}</td>
                <td className="px-5 py-3.5 text-gray-500 hidden md:table-cell">{s.display_order}</td>
                <td className="px-5 py-3.5">
                  <button onClick={() => handleToggle(s.id)}
                    className={`px-2.5 py-1 rounded-full text-xs font-medium transition-colors ${s.is_active ? 'bg-green-100 text-green-700 hover:bg-green-200' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'}`}>
                    {s.is_active ? 'Aktif' : 'Pasif'}
                  </button>
                </td>
                <td className="px-5 py-3.5 text-right">
                  <div className="flex items-center justify-end gap-2">
                    <button onClick={() => openEdit(s)} className="p-2 text-gray-500 hover:text-primary hover:bg-gray-100 rounded-lg transition-colors">
                      <FaEdit size={14} />
                    </button>
                    <button onClick={() => handleDelete(s.id)} className="p-2 text-gray-500 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors">
                      <FaTrash size={14} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {services.length === 0 && <div className="text-center py-12 text-gray-400">Henüz hizmet eklenmemiş</div>}
      </div>

      {modal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-2xl shadow-2xl max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between px-6 py-4 border-b flex-shrink-0">
              <h3 className="font-semibold text-gray-700">{editId ? 'Hizmet Düzenle' : 'Yeni Hizmet'}</h3>
              <button onClick={close} className="text-gray-400 hover:text-gray-600"><FaTimes /></button>
            </div>
            <div className="p-6 space-y-5 overflow-y-auto">

              {/* Ad + URL */}
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Hizmet Adı *</label>
                  <input value={form.name}
                    onChange={e => setForm(f => ({ ...f, name: e.target.value, slug: !editId ? autoSlug(e.target.value) : f.slug }))}
                    className="input-field" placeholder="Hizmet adı" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">URL Kodu *</label>
                  <input value={form.slug} readOnly
                    className="input-field font-mono text-sm bg-gray-50 cursor-default text-gray-500" placeholder="hizmet-url-kodu" />
                </div>
              </div>

              {/* Sıra + Durum */}
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Sıra</label>
                  <input type="number" value={form.display_order} onChange={e => setForm(f => ({ ...f, display_order: +e.target.value }))}
                    className="input-field" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Durum</label>
                  <select value={form.is_active} onChange={e => setForm(f => ({ ...f, is_active: +e.target.value }))} className="input-field">
                    <option value={1}>Aktif</option>
                    <option value={0}>Pasif</option>
                  </select>
                </div>
              </div>

              {/* İkon Seçici */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  İkon Seç
                  <span className="ml-2 inline-flex items-center gap-1.5 text-secondary font-normal text-xs bg-secondary/10 px-2 py-0.5 rounded-full">
                    <selectedIconDef.icon size={11} /> {selectedIconDef.label} seçili
                  </span>
                </label>
                <div className="grid grid-cols-4 sm:grid-cols-8 gap-2">
                  {ICON_LIST.map(({ name, icon: Icon, label }) => (
                    <button
                      key={name}
                      type="button"
                      onClick={() => setForm(f => ({ ...f, icon: name }))}
                      title={label}
                      className={`flex flex-col items-center gap-1 p-2.5 rounded-lg border-2 transition-all text-xs ${
                        form.icon === name
                          ? 'border-secondary bg-secondary/10 text-secondary'
                          : 'border-gray-200 hover:border-gray-300 text-gray-500 hover:text-gray-700'
                      }`}
                    >
                      <Icon size={18} />
                      <span className="truncate w-full text-center leading-tight" style={{ fontSize: '10px' }}>{label}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Açıklamalar */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Kısa Açıklama</label>
                <textarea value={form.short_description} onChange={e => setForm(f => ({ ...f, short_description: e.target.value }))}
                  rows={2} className="input-field resize-none" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Tam Açıklama</label>
                <textarea value={form.full_description} onChange={e => setForm(f => ({ ...f, full_description: e.target.value }))}
                  rows={4} className="input-field resize-none" />
              </div>

              {/* Özellikler */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-gray-700">Özellikler</label>
                  <button type="button" onClick={addFeature}
                    className="text-xs text-secondary font-medium flex items-center gap-1 hover:underline">
                    <FaPlus size={10} /> Satır Ekle
                  </button>
                </div>
                {form.features.length > 0 ? (
                  <div className="space-y-2">
                    {form.features.map((feat, i) => (
                      <div key={i} className="flex gap-2 items-center">
                        <input
                          value={feat}
                          onChange={e => updateFeature(i, e.target.value)}
                          placeholder={`Özellik ${i + 1}`}
                          className="input-field flex-1 text-sm py-2"
                        />
                        <button type="button" onClick={() => removeFeature(i)}
                          className="p-2 text-gray-400 hover:text-red-500 transition-colors flex-shrink-0">
                          <FaTimes size={13} />
                        </button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="border-2 border-dashed border-gray-200 rounded-lg p-3 text-center text-sm text-gray-400">
                    Özellik yok — "Satır Ekle" ile ekleyebilirsiniz
                  </div>
                )}
              </div>

              {/* Görsel */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Görsel</label>
                <div className="flex gap-2">
                  <input value={form.image_url} onChange={e => setForm(f => ({ ...f, image_url: e.target.value }))}
                    className="input-field flex-1" placeholder="URL veya dosya yükleyin" />
                  <label className="btn-secondary text-sm py-3 px-4 cursor-pointer flex-shrink-0">
                    {uploading ? '...' : <><FaUpload size={13} /> Yükle</>}
                    <input type="file" accept="image/*" onChange={handleImageUpload} className="hidden" />
                  </label>
                </div>
                {form.image_url && (
                  <img src={form.image_url} alt="" className="mt-2 h-20 rounded-lg object-cover" />
                )}
              </div>

              {error && <div className="bg-red-50 border border-red-200 text-red-600 rounded-lg px-4 py-2 text-sm">{error}</div>}
            </div>
            <div className="flex gap-3 px-6 pb-6 flex-shrink-0">
              <button onClick={close} className="flex-1 px-4 py-2.5 border border-gray-200 rounded-lg text-gray-600 hover:bg-gray-50 text-sm font-medium">İptal</button>
              <button onClick={handleSave} disabled={saving} className="flex-1 btn-primary justify-center py-2.5 text-sm disabled:opacity-60">
                {saving ? 'Kaydediliyor...' : <><FaSave size={13} /> Kaydet</>}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
